import {createCanvasBridgeHost, type CanvasBridgeHost} from '@benchling/canvas-bridge-host';
import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import navigateToURL from 'lib/navigateToURL';
import {CenteredSpinner} from 'ui-library/Spinner';

interface CanvasPreviewFrameProps {
  /** Optionally intercept Canvas navigation. Return true to prevent the default navigation. */
  onNavigateToURL?: (url: string) => boolean;
  signedUrl: string;
  sdkVersion: string;
}

const useStyles = createStyles((theme) => ({
  frameContainer: {
    borderRadius: theme.radius.md,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  iframe: {
    border: 'none',
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  iframeLoading: {
    visibility: 'hidden',
  },
  loadingOverlay: {
    alignItems: 'center',
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    position: 'absolute',
  },
}));

export default function CanvasPreviewFrame({
  onNavigateToURL,
  signedUrl,
  sdkVersion,
}: CanvasPreviewFrameProps): JSX.Element {
  // Key the URL-specific component so URL changes remount the iframe and its bridge lifecycle.
  return (
    <CanvasPreviewFrameForUrl
      key={`${signedUrl}:${sdkVersion}`}
      onNavigateToURL={onNavigateToURL}
      signedUrl={signedUrl}
      sdkVersion={sdkVersion}
    />
  );
}

function CanvasPreviewFrameForUrl({
  onNavigateToURL,
  signedUrl,
  sdkVersion,
}: CanvasPreviewFrameProps): JSX.Element {
  const {classes, cx} = useStyles();
  const bridgeHostRef = useRef<CanvasBridgeHost | null>(null);
  const hasLoadedRef = useRef(false);
  const onNavigateToURLRef = useRef(onNavigateToURL);
  onNavigateToURLRef.current = onNavigateToURL;
  const [frameRemountKey, setFrameRemountKey] = useState(0);
  const [isFrameLoading, setIsFrameLoading] = useState(true);
  const isCanvasCoreSdkEnabled = BenchlingConfig.ENABLE_CANVAS_CORE_SDK;

  const closeBridgeHost = useCallback(() => {
    bridgeHostRef.current?.close();
    bridgeHostRef.current = null;
  }, []);

  // A layout effect closes the bridge during unmount, before the iframe is detached.
  useLayoutEffect(() => closeBridgeHost, [closeBridgeHost]);

  const handleNavigateToURL = useCallback((relativePath: string): boolean => {
    const navigationHandled = onNavigateToURLRef.current?.(relativePath) ?? false;
    return navigationHandled || navigateToURL(relativePath);
  }, []);

  const handleLoad = (event: React.SyntheticEvent<HTMLIFrameElement>): void => {
    if (!isCanvasCoreSdkEnabled || !sdkVersion) {
      setIsFrameLoading(false);
      return;
    }
    closeBridgeHost();
    // An iframe navigation emits another load without changing the src attribute, even if it
    // reaches a different site. Remount before creating a new bridge.
    if (hasLoadedRef.current) {
      hasLoadedRef.current = false;
      setIsFrameLoading(true);
      setFrameRemountKey((key) => key + 1);
      return;
    }
    hasLoadedRef.current = true;
    setIsFrameLoading(false);
    const canvasWindow = event.currentTarget.contentWindow;
    if (canvasWindow == null) {
      return;
    }

    const targetUrl = new URL(signedUrl);
    if (targetUrl.protocol !== 'https:') {
      return;
    }

    bridgeHostRef.current = createCanvasBridgeHost(canvasWindow, targetUrl.origin, {
      navigateToURL: handleNavigateToURL,
    });
  };

  const handleError = (): void => {
    setIsFrameLoading(false);
    if (!isCanvasCoreSdkEnabled || !sdkVersion) {
      return;
    }
    closeBridgeHost();
  };

  return (
    <div
      className={classes.frameContainer}
      {...getTestProps({component: 'CanvasPreviewFrame', element: 'root'})}
    >
      {isFrameLoading && (
        <div className={classes.loadingOverlay}>
          <CenteredSpinner />
        </div>
      )}
      <iframe
        key={frameRemountKey}
        sandbox="allow-same-origin allow-scripts"
        src={signedUrl}
        className={cx(classes.iframe, isFrameLoading && classes.iframeLoading)}
        title="Canvas preview"
        onLoad={handleLoad}
        // TODO(BNCH-176381): Surface bridge creation failures because iframe navigation failures do not reliably emit error events.
        onError={handleError}
      />
    </div>
  );
}
